import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";

import RestaurantBootstrapLayout from "@/components/templates/RestaurantBootstrapLayout";
import {
  AuthRole,
  type AuthStage,
  type RestaurantAccess,
  RestaurantRole,
  StageAuth,
  type UserRole,
} from "@/store/AuthStore";

import { BRANCHES, BRANDS, GROUPS, PATHS } from "../path";
import { ProtectedRoute } from "../ProtectedRoute";
import { RestaurantRedirect } from "./RestaurantRedirect";

const RestaurantBusinessManagementPage = lazy(
  () =>
    import(
      "@/modules/RestaurantBusinessManagement/RestaurantBusinessManagement"
    )
);
const RestaurantMenuPage = lazy(
  () => import("@/modules/RestaurantMenuPage/RestaurantMenuPage")
);
const NewDishForm = lazy(
  () => import("@/components/units/NewDishForm/NewDishForm")
);
const LayoutWithMenu = lazy(
  () => import("@/components/templates/LayoutWithMenu")
);
const RestaurantOverview = lazy(
  () => import("@/modules/RestaurantOverview/RestaurantOverview")
);
const RestaurantFunding = lazy(
  () => import("@/modules/RestaurantFunding/RestaurantFunding")
);
const RestaurantUsers = lazy(
  () => import("@/modules/RestaurantUsers/RestaurantUsers")
);
const RestaurantReservations = lazy(
  () => import("@/modules/RestaurantReservations/RestaurantReservations")
);
const RestaurantBrandSettings = lazy(
  () => import("@/modules/RestaurantSettings/RestaurantBrandSettings")
);
const RestaurantBranchSettings = lazy(
  () => import("@/modules/RestaurantSettings/RestaurantBranchSettings")
);
const RestaurantReviews = lazy(
  () => import("@/modules/RestaurantReviews/RestaurantReviews")
);
const RestaurantDiners = lazy(
  () => import("@/modules/RestaurantDiners/RestaurantDiners")
);
const DinerPage = lazy(() => import("@/modules/RestaurantDiners/DinerPage"));
const RestaurantTransactions = lazy(
  () => import("@/modules/RestaurantTransactions/RestaurantTransactions")
);
const OrderPage = lazy(() => import("@/modules/RestaurantOrderPage/OrderPage"));
const ProfileSettings = lazy(
  () => import("@/modules/ProfileSettings/ProfileSettings")
);
const BranchCreatePage = lazy(
  () => import("@/modules/BranchStepsRestaurant/BranchStepsPageRestaurant")
);
const BrandCreatePage = lazy(
  () => import("@/modules/BrandStepsRestaurant/BrandStepsPageRestaurant")
);
const BranchCreateGroupPage = lazy(
  () => import("@/modules/BranchStepsRestaurant/BranchStepsGroupRestaurant")
);
const BranchBusinessManagementPage = lazy(
  () => import("@/modules/BranchStepsRestaurant/BranchStepsBusinessManagement")
);
const CreditBalancePage = lazy(
  () => import("@/modules/RestaurantCreditBalance/RestaurantCreditBalance")
);

const withProtection = (
  element: React.ReactNode,
  role: UserRole,
  stage: AuthStage = StageAuth.AUTHENTICATED,
  access?: RestaurantAccess
) => (
  <ProtectedRoute
    requiredStage={stage}
    requiredRole={role}
    requiredAccess={access}
  >
    {element}
  </ProtectedRoute>
);

const brandSteps = [
  PATHS.RESTAURANT.GROUP.NEW_BRAND_DETAILS,
  PATHS.RESTAURANT.GROUP.NEW_BRAND_CONTACTS,
  PATHS.RESTAURANT.GROUP.NEW_BRAND_MENU,
  PATHS.RESTAURANT.GROUP.NEW_BRAND_BRANCHES,
].map(path => ({
  path,
  element: withProtection(
    <BrandCreatePage />,
    AuthRole.RESTAURANT,
    StageAuth.AUTHENTICATED,
    RestaurantRole.GROUP
  ),
}));

const branchStepsBrand = [
  PATHS.RESTAURANT.BRAND.NEW_BRANCH_DETAILS,
  PATHS.RESTAURANT.BRAND.NEW_BRANCH_HOURS,
  PATHS.RESTAURANT.BRAND.NEW_BRANCH_VIBES,
  PATHS.RESTAURANT.BRAND.NEW_BRANCH_AMENITIES,
  PATHS.RESTAURANT.BRAND.NEW_BRANCH_MEDIA,
].map(path => ({
  path,
  element: withProtection(
    <BranchCreatePage />,
    AuthRole.RESTAURANT,
    StageAuth.AUTHENTICATED,
    RestaurantRole.BRAND
  ),
}));

const branchStepsGroup = [
  PATHS.RESTAURANT.GROUP.NEW_BRANCH_DETAILS,
  PATHS.RESTAURANT.GROUP.NEW_BRANCH_HOURS,
  PATHS.RESTAURANT.GROUP.NEW_BRANCH_VIBES,
  PATHS.RESTAURANT.GROUP.NEW_BRANCH_AMENITIES,
  PATHS.RESTAURANT.GROUP.NEW_BRANCH_MEDIA,
].map(path => ({
  path,
  element: withProtection(
    <BranchCreateGroupPage />,
    AuthRole.RESTAURANT,
    StageAuth.AUTHENTICATED,
    RestaurantRole.GROUP
  ),
}));

const branchStepsBusinessManagement = [
  PATHS.RESTAURANT.BRANCH.BUSINESS_MANAGEMENT_DETAILS,
  PATHS.RESTAURANT.BRANCH.BUSINESS_MANAGEMENT_HOURS,
  PATHS.RESTAURANT.BRANCH.BUSINESS_MANAGEMENT_VIBES,
  PATHS.RESTAURANT.BRANCH.BUSINESS_MANAGEMENT_AMENITIES,
  PATHS.RESTAURANT.BRANCH.BUSINESS_MANAGEMENT_MEDIA,
].map(path => ({
  path,
  element: withProtection(
    <BranchBusinessManagementPage />,
    AuthRole.RESTAURANT,
    StageAuth.AUTHENTICATED,
    RestaurantRole.BRANCH
  ),
}));

const RESTAURANT_ROOT_PATHS: Record<NonNullable<RestaurantAccess>, string> = {
  GROUP: PATHS.RESTAURANT.GROUP.ROOT,
  BRAND: PATHS.RESTAURANT.BRAND.ROOT,
  BRANCH: PATHS.RESTAURANT.BRANCH.ROOT,
};

const layoutWithChildren = (
  access: RestaurantRole,
  children: RouteObject[]
): RouteObject => ({
  path: RESTAURANT_ROOT_PATHS[access],
  element: withProtection(
    <LayoutWithMenu />,
    AuthRole.RESTAURANT,
    StageAuth.AUTHENTICATED,
    access
  ),
  children,
});

export const restaurantRoutes: RouteObject[] = [
  {
    element: <RestaurantBootstrapLayout />,
    children: [
      {
        path: PATHS.RESTAURANT.ROOT,
        element: withProtection(<RestaurantRedirect />, AuthRole.RESTAURANT),
      },
      {
        path: PATHS.RESTAURANT.GROUP.ROOT,
        element: withProtection(
          <RestaurantBusinessManagementPage />,
          AuthRole.RESTAURANT,
          StageAuth.AUTHENTICATED,
          RestaurantRole.GROUP
        ),
      },
      {
        path: PATHS.RESTAURANT.BRAND.ROOT,
        element: withProtection(
          <RestaurantBusinessManagementPage />,
          AuthRole.RESTAURANT,
          StageAuth.AUTHENTICATED,
          RestaurantRole.BRAND
        ),
      },

      ...brandSteps,
      ...branchStepsBrand,
      ...branchStepsGroup,
      ...branchStepsBusinessManagement,

      {
        path: PATHS.RESTAURANT.GROUP.NEW_BRAND_DISH,
        element: withProtection(
          <NewDishForm isWide={false} />,
          AuthRole.RESTAURANT,
          StageAuth.AUTHENTICATED,
          RestaurantRole.GROUP
        ),
      },

      layoutWithChildren(RestaurantRole.GROUP, [
        { path: GROUPS.OVERVIEW, element: <RestaurantOverview /> },
        { path: GROUPS.TRANSACTIONS, element: <RestaurantTransactions /> },
        { path: GROUPS.ORDER(":id"), element: <OrderPage /> },
        { path: GROUPS.CREDIT_BALANCE, element: <CreditBalancePage /> },
        { path: GROUPS.FUNDING, element: <RestaurantFunding /> },
        { path: GROUPS.USERS, element: <RestaurantUsers /> },
        { path: GROUPS.DINERS, element: <RestaurantDiners /> },
        { path: GROUPS.ONE_DINER(":id"), element: <DinerPage /> },
        {
          path: PATHS.RESTAURANT.GROUP.PROFILE_SETTINGS,
          element: <ProfileSettings />,
        },
      ]),

      layoutWithChildren(RestaurantRole.BRAND, [
        { path: BRANDS.OVERVIEW, element: <RestaurantOverview /> },
        { path: BRANDS.DINERS, element: <RestaurantDiners /> },
        { path: BRANDS.ONE_DINER(":id"), element: <DinerPage /> },
        { path: BRANDS.FUNDING, element: <RestaurantFunding /> },
        { path: BRANDS.MENU, element: <RestaurantMenuPage /> },
        { path: BRANDS.NEW_DISH, element: <NewDishForm /> },
        { path: BRANDS.RESERVATIONS, element: <RestaurantReservations /> },
        { path: BRANDS.TRANSACTIONS, element: <RestaurantTransactions /> },
        { path: BRANDS.ORDER(":id"), element: <OrderPage /> },
        { path: BRANDS.SETTINGS, element: <RestaurantBrandSettings /> },
        { path: BRANDS.USERS, element: <RestaurantUsers /> },
        {
          path: PATHS.RESTAURANT.BRAND.PROFILE_SETTINGS,
          element: <ProfileSettings />,
        },
      ]),

      layoutWithChildren(RestaurantRole.BRANCH, [
        { path: "", element: <Navigate to={BRANCHES.TRANSACTIONS} replace /> },
        { path: BRANCHES.DINERS, element: <RestaurantDiners /> },
        { path: BRANCHES.ONE_DINER(":id"), element: <DinerPage /> },
        { path: BRANCHES.FUNDING, element: <RestaurantFunding /> },
        { path: BRANCHES.MENU, element: <RestaurantMenuPage /> },
        { path: BRANCHES.NEW_DISH, element: <NewDishForm /> },
        { path: BRANCHES.OVERVIEW, element: <RestaurantOverview /> },
        { path: BRANCHES.RESERVATIONS, element: <RestaurantReservations /> },
        { path: BRANCHES.REVIEWS, element: <RestaurantReviews /> },
        { path: BRANCHES.SETTINGS, element: <RestaurantBranchSettings /> },
        { path: BRANCHES.TRANSACTIONS, element: <RestaurantTransactions /> },
        { path: BRANCHES.ORDER(":id"), element: <OrderPage /> },
        { path: BRANCHES.USERS, element: <RestaurantUsers /> },
        {
          path: PATHS.RESTAURANT.BRANCH.PROFILE_SETTINGS,
          element: <ProfileSettings />,
        },
      ]),
    ],
  },
];
